import React, { useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import Loading from "../common/Loading";
import { ACCENT } from "./shared";
import { trackApi, trackErrorMessage } from "./trackApi";
import { PERIOD_OPTIONS } from "./StudioDashboard";
import AudienceCards from "./AudienceCards";

// Artist Studio > Track detail > Performance (FSD 6.3.3): streams, saves and videos using the sound over time, licences by usage
// class, and how the earnings split between streaming and licensing, for the period the artist picks. Every number comes from one
// API call, and the cards are the sums of the chart's own points, so the chart and the cards always agree. A track that went live
// inside the period starts on its go-live day. Likes are not shown: the product has no track likes yet.

const CLASS_LABELS = { personal: "Personal", creator: "Creator", business: "Business" };
const SERIES = [
  { key: "streams", label: "Streams", color: ACCENT },
  { key: "saves", label: "Saves", color: "#b8560f" },
  { key: "videos", label: "Videos using this sound", color: "#2563eb" },
];

const number = (n) => Number(n || 0).toLocaleString();
const shortDate = (iso, group) => {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return group === "month"
    ? d.toLocaleDateString(undefined, { month: "short", year: "2-digit", timeZone: "UTC" })
    : d.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
};
const money = (minor, currency) => {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: currency || "USD" }).format((Number(minor) || 0) / 100);
  } catch (e) {
    return `${((Number(minor) || 0) / 100).toFixed(2)}`;
  }
};

function Card({ label, value, testId }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
      <p className="mt-1 text-2xl font-bold text-gray-900" data-testid={testId}>{value}</p>
    </div>
  );
}

export default function TrackPerformance({ trackId }) {
  const [period, setPeriod] = useState("28d");
  const [state, setState] = useState({ status: "loading", data: null, error: "" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ status: "loading", data: s.data, error: "" }));
    trackApi.performance(trackId, period).then(
      (data) => { if (!cancelled) setState({ status: "ready", data, error: "" }); },
      (e) => { if (!cancelled) setState({ status: "error", data: null, error: trackErrorMessage(e, "We could not load this track's performance. Please try again.") }); }
    );
    return () => { cancelled = true; };
  }, [trackId, period, attempt]);

  const { data } = state;
  const earnings = data && data.earnings;
  const total = earnings ? Number(earnings.total_minor) : 0;
  const streamingShare = total > 0 ? Math.round((Number(earnings.streaming_minor) / total) * 100) : 0;
  const classRows = data ? Object.keys(CLASS_LABELS).map((k) => ({ name: CLASS_LABELS[k], licences: data.licences.by_class[k] || 0 })) : [];

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-6 mt-6" aria-label="Performance" data-testid="track-performance">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-lg font-bold text-gray-900">Performance</h3>
        <div role="group" aria-label="Period" className="flex flex-wrap gap-2">
          {PERIOD_OPTIONS.map((o) => (
            <button
              key={o.key}
              type="button"
              aria-pressed={period === o.key}
              onClick={() => setPeriod(o.key)}
              className={`rounded-full border px-3 py-1 text-sm font-semibold ${period === o.key ? "text-white" : "border-gray-300 text-gray-700"}`}
              style={period === o.key ? { backgroundColor: ACCENT, borderColor: ACCENT } : undefined}
              data-testid={`perf-period-${o.key}`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {state.status === "error" && (
        <p className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert" data-testid="perf-error">
          {state.error} <button type="button" className="underline font-semibold" onClick={() => setAttempt((n) => n + 1)}>Try again</button>
        </p>
      )}
      {state.status === "loading" && !data && <div className="flex justify-center py-10"><Loading /></div>}

      {data && (
        <div className={state.status === "loading" ? "opacity-60" : ""} aria-busy={state.status === "loading"}>
          {data.period.starts_at_go_live && data.period.key !== "all" && (
            <p className="mt-3 text-sm text-gray-500" data-testid="perf-golive">This track went live on {shortDate(data.track.went_live_at.slice(0, 10), "day")}, so the chart starts there.</p>
          )}
          <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card label="Streams" value={number(data.totals.streams)} testId="perf-streams" />
            <Card label="Saves" value={number(data.totals.saves)} testId="perf-saves" />
            <Card label="Videos using this sound" value={number(data.totals.videos)} testId="perf-videos" />
            <Card label="Licences sold" value={number(data.licences.total)} testId="perf-licences" />
          </div>

          <div className="mt-6 h-72" data-testid="perf-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.series.map((p) => ({ ...p, label: shortDate(p.date, data.period.group) }))} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} minTickGap={24} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} width={40} />
                <Tooltip />
                <Legend />
                {SERIES.map((s) => <Line key={s.key} type="monotone" dataKey={s.key} name={s.label} stroke={s.color} strokeWidth={2} dot={false} />)}
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            <div>
              <h4 className="text-sm font-bold text-gray-900">Licences by usage class</h4>
              {data.licences.total === 0 ? (
                <p className="mt-3 text-sm text-gray-500" data-testid="perf-licences-empty">No licences sold in this period.</p>
              ) : (
                <div className="mt-2 h-48" data-testid="perf-licence-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={classRows} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 12 }} width={32} />
                      <Tooltip />
                      <Bar dataKey="licences" name="Licences" fill={ACCENT} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
            <div>
              <h4 className="text-sm font-bold text-gray-900">Earnings in this period</h4>
              <p className="mt-2 text-2xl font-bold text-gray-900" data-testid="perf-earnings-total">{money(earnings.total_minor, earnings.currency)}</p>
              <div className="mt-3 flex h-3 overflow-hidden rounded-full bg-gray-200" aria-hidden="true">
                <div style={{ width: `${total > 0 ? streamingShare : 0}%`, backgroundColor: ACCENT }} />
                <div style={{ width: `${total > 0 ? 100 - streamingShare : 0}%`, backgroundColor: "#b8560f" }} />
              </div>
              <dl className="mt-3 space-y-1 text-sm">
                <div className="flex justify-between"><dt className="text-gray-600"><span className="inline-block h-2 w-2 rounded-full mr-2" style={{ backgroundColor: ACCENT }} />Streaming royalty</dt><dd className="font-semibold" data-testid="perf-earnings-streaming">{money(earnings.streaming_minor, earnings.currency)}</dd></div>
                <div className="flex justify-between"><dt className="text-gray-600"><span className="inline-block h-2 w-2 rounded-full mr-2" style={{ backgroundColor: "#b8560f" }} />Licensing (after commission, net of refunds)</dt><dd className="font-semibold" data-testid="perf-earnings-licensing">{money(earnings.licensing_minor, earnings.currency)}</dd></div>
              </dl>
            </div>
          </div>
          <AudienceCards period={period} trackId={trackId} />
        </div>
      )}
    </section>
  );
}
